// Contesto allegato agli eventi: user, tag, extra, context, trace_id e cronologia
// breadcrumb. Specchio di `CloseYourIt::Scope` (gem) e `Scope` (dart), adattato a
// un holder globale singolo (il browser è single-thread). La correlazione
// per-operazione si ottiene via [traceId].

import { Breadcrumb, BreadcrumbBuffer } from './breadcrumbs'
import type { BreadcrumbInput } from './breadcrumbs'
import { scrub } from './scrubber'
import type { EventPayload, UserContext } from './types'

export class Scope {
  readonly breadcrumbs: BreadcrumbBuffer

  private user: Record<string, unknown> | undefined
  private tags: Record<string, string> = {}
  private extra: Record<string, unknown> = {}
  private contexts: Record<string, Record<string, unknown>> = {}
  private request: Record<string, unknown> | undefined

  /// ID di correlazione condiviso da log/errori della stessa operazione/pagina.
  traceId: string | undefined

  constructor(maxBreadcrumbs = 50) {
    this.breadcrumbs = new BreadcrumbBuffer(maxBreadcrumbs)
  }

  setUser(user: UserContext | null): void {
    this.user = user === null ? undefined : { ...user }
  }

  setTag(key: string, value: string): void {
    this.tags[key] = value
  }

  setTags(tags: Record<string, string>): void {
    Object.assign(this.tags, tags)
  }

  setExtra(key: string, value: unknown): void {
    this.extra[key] = value
  }

  setContext(key: string, value: Record<string, unknown>): void {
    this.contexts[key] = value
  }

  setTraceId(id: string | undefined): void {
    this.traceId = id
  }

  /// Request context manuale: vince su quello rilevato automaticamente.
  setRequest(request: Record<string, unknown> | null): void {
    this.request = request === null ? undefined : { ...request }
  }

  addBreadcrumb(input: BreadcrumbInput): void {
    this.breadcrumbs.add(new Breadcrumb(input))
  }

  clear(): void {
    this.user = undefined
    this.tags = {}
    this.extra = {}
    this.contexts = {}
    this.request = undefined
    this.traceId = undefined
    this.breadcrumbs.clear()
  }

  /// Serializza in chiavi Sentry, includendo solo le sezioni valorizzate.
  /// Tags/extra/contexts/request passano per lo scrubber (difesa in profondità).
  /// `user` ha la sua policy dedicata (parità gem): senza `sendPii` si riduce a
  /// `{id}` (email & co. non partono proprio); con `sendPii` viaggia intero —
  /// impostarlo è una dichiarazione esplicita d'intento.
  toEventHash(opts: { sendPii?: boolean } = {}): EventPayload {
    const hash: EventPayload = {}

    if (this.user) {
      const user = (opts.sendPii ?? false) ? { ...this.user } : pickId(this.user)
      if (user) hash['user'] = user
    }
    if (Object.keys(this.tags).length > 0) hash['tags'] = scrub(this.tags)
    if (Object.keys(this.extra).length > 0) hash['extra'] = scrub(this.extra)
    if (Object.keys(this.contexts).length > 0) {
      hash['contexts'] = scrub(this.contexts)
    }
    if (this.request) hash['request'] = scrub(this.request)
    const crumbs = this.breadcrumbs.toJSON()
    if (crumbs) hash['breadcrumbs'] = crumbs

    return hash
  }
}

function pickId(user: Record<string, unknown>): Record<string, unknown> | undefined {
  return 'id' in user ? { id: user['id'] } : undefined
}
